import { drop, entries, groupBy, isEmpty, isEqual, isString, take } from "lodash";
import type { TranslationKey } from "../_generated/translation-keys";
import { customFieldLabelMapping } from "../candidate.field-label.mapping";
import { typedKeys } from "../lodash-extra";
import { customFieldDefinitions } from "./customFields.definitions";
import type { CustomFieldDefinition, CustomFieldScalarDefinition, CustomFieldText } from "./customFields.definitions";
import type { ICustomFieldsModel } from "./customFields.model";

const CUSTOM_FIELDS_DATA_PATH = "data?.candidate?.os?.profile?.customFields";
const ARRAY_ITEM_HOP = "?.[i]?.";
const CANDIDATE_ROOT_SEGMENTS = ["data", "candidate"];

type CustomFieldGroup = keyof ICustomFieldsModel;

export interface CustomFieldLeaf {
  group: CustomFieldGroup;
  key: string;
  itemKey?: string;
  // `data?.candidate?.os?.profile?.customFields?.<key>` (an array item: `…?.<key>?.[i]?.<itemKey>`), as a process expression reads it.
  path: string;
  // What the form shows on the way to the field: its section, the array (for an item), the field.
  labels: CustomFieldText[];
  definition: CustomFieldScalarDefinition;
}

interface CustomFieldDeclaration {
  group: CustomFieldGroup;
  key: string;
  sectionLabel: CustomFieldText;
  definition: CustomFieldDefinition;
  labelKeys: TranslationKey | Record<string, TranslationKey>;
}

const declarations: CustomFieldDeclaration[] = typedKeys(customFieldDefinitions).flatMap((group) => {
  const { sectionLabel } = customFieldDefinitions[group];
  const fields: Record<string, CustomFieldDefinition> = customFieldDefinitions[group].fields;
  const labelKeys: Record<string, TranslationKey | Record<string, TranslationKey>> = customFieldLabelMapping[group];
  return entries(fields).map(([key, definition]) => ({
    group,
    key,
    sectionLabel,
    definition,
    labelKeys: labelKeys[key],
  }));
});

const leaves = declarations.flatMap(toLeaves);
const leavesByKey = groupBy(leaves, ({ key }) => key);
const leafByPlainPath = new Map(leaves.map((leaf) => [toPlainPath(leaf.path), leaf]));

export function getCustomFieldDataPath(key: string): string {
  return `${CUSTOM_FIELDS_DATA_PATH}?.${key}`;
}

// The readable leaves of a custom field: the field itself, or every item property of an array field.
export function getCustomFieldLeaves(key: string): CustomFieldLeaf[] {
  return leavesByKey[key] ?? [];
}

// The custom field a data path reads, in any spelling (`?.` or `.`, with or without `data.candidate.`, any array index); null otherwise.
export function findCustomFieldLeaf(path: string): CustomFieldLeaf | null {
  return leafByPlainPath.get(toPlainPath(path)) ?? null;
}

export function resolveCustomFieldText(text: CustomFieldText, translate: (key: TranslationKey) => string): string {
  return "translationKey" in text ? translate(text.translationKey) : text.text;
}

function toLeaves({ group, key, sectionLabel, definition, labelKeys }: CustomFieldDeclaration): CustomFieldLeaf[] {
  const path = getCustomFieldDataPath(key);
  if (definition.valueType !== "array") {
    const labels = isString(labelKeys) ? [sectionLabel, { translationKey: labelKeys }] : [sectionLabel];
    return [{ group, key, path, labels, definition }];
  }

  return entries(definition.items).map(([itemKey, itemDefinition]) => {
    const itemLabelKey = isString(labelKeys) ? null : labelKeys[itemKey];
    const itemLabels = isEmpty(itemLabelKey) ? [] : [{ translationKey: itemLabelKey }];
    return {
      group,
      key,
      itemKey,
      path: `${path}${ARRAY_ITEM_HOP}${itemKey}`,
      labels: [sectionLabel, definition.label, ...itemLabels],
      definition: itemDefinition,
    };
  });
}

function toPlainPath(path: string): string {
  const segments = path
    .replace(/\?\./g, ".")
    .replace(/\[[^\]]*\]/g, "")
    .split(".")
    .filter((segment) => !isEmpty(segment));
  const relative = isEqual(take(segments, CANDIDATE_ROOT_SEGMENTS.length), CANDIDATE_ROOT_SEGMENTS)
    ? drop(segments, CANDIDATE_ROOT_SEGMENTS.length)
    : segments;
  return relative.join(".");
}
